// Missões e baú do dia (lote 3, item 13). A tela Missões tem o baú do dia com o calendário de 7 dias (o conteúdo
// aparece antes de abrir, nunca é sorteado), o baú de retorno, as 3 missões do dia com a troca grátis e as 3 da
// semana. No Início, o resumo; no fim de partida, as missões que a partida fechou (GA4 missao_concluida).
// Tudo calculado no servidor (packages/gamificacao); aqui só mostra.
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useState } from 'react';
import { cores } from '@truco6/ui';
import type { ConteudoDoBau, MissaoFechada, MissaoNaTela, PainelMissoes } from '@truco6/gamificacao';
import { abrirBau, lerMissoes, trocarMissao } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Tela, Topo } from '../telas/comum.tsx';
import { Icone } from '../telas/planos-comum.tsx';
import { Tento } from '../telas/tentos-comum.tsx';
import { Progresso } from './badges.tsx';
import { PasseNoPerfil } from './passe.tsx';

// O baú: madeira com as ferragens douradas; aberto, a tampa sobe e aparece o brilho.
export function BauIcone({ tamanho = 40, aberto = false }: { tamanho?: number; aberto?: boolean }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 48 48" aria-hidden="true" className="bau-icone">
      {aberto && <ellipse cx="24" cy="22" rx="15" ry="6" fill={cores.gold400} opacity=".55" />}
      <rect x="7" y="22" width="34" height="19" rx="3" fill={cores.wood500} stroke={cores.wood700} strokeWidth="1.5" />
      {aberto
        ? <path d="M9 21 L12 9 H36 L39 21 Z" fill={cores.wood500} stroke={cores.wood700} strokeWidth="1.5" />
        : <path d="M7 22 V17 C7 12 11 9 16 9 H32 C37 9 41 12 41 17 V22 Z" fill={cores.wood500} stroke={cores.wood700} strokeWidth="1.5" />}
      <rect x="7" y="21" width="34" height="3.5" fill={cores.gold600} />
      <rect x="13" y={aberto ? 9 : 9.5} width="3.5" height="31" fill={cores.gold600} opacity=".9" />
      <rect x="31.5" y={aberto ? 9 : 9.5} width="3.5" height="31" fill={cores.gold600} opacity=".9" />
      <rect x="20.5" y="20" width="7" height="8" rx="1.5" fill={cores.gold400} stroke={cores.gold600} />
      <circle cx="24" cy="24" r="1.3" fill={cores.wood700} />
    </svg>
  );
}

const conteudo = (c: ConteudoDoBau) => (c.tentos ? `${c.tentos} Tentos` : `+${c.rodagem} de Rodagem`);

function DiaDoCalendario({ c, hoje, aberto }: { c: ConteudoDoBau; hoje: number; aberto: boolean }) {
  const passado = c.dia < hoje || (c.dia === hoje && aberto), atual = c.dia === hoje;
  return (
    <li className={(atual ? 'hoje' : '') + (passado ? ' feito' : '')} aria-label={`Dia ${c.dia}: ${conteudo(c)}${passado ? ', aberto' : atual ? ', hoje' : ''}`}>
      <small>Dia {c.dia}</small>
      {c.tentos ? <span className="premio-bau tentos"><Tento tamanho={18} />{c.tentos}</span> : <span className="premio-bau">+{c.rodagem}</span>}
    </li>
  );
}

// O baú do dia, com o calendário. O botão abre o de hoje depois da primeira partida do dia.
function BauDoDia({ m, ocupado, abrir }: { m: PainelMissoes; ocupado: boolean; abrir: (tipo: 'dia' | 'retorno') => void }) {
  const b = m.bau, hoje = b.calendario.find((c) => c.dia === b.dia)!, amanha = b.calendario.find((c) => c.dia === (b.dia % 7) + 1)!;
  return (
    <section className="cartao bau-dia" aria-label="Baú do dia">
      <div className="bau-cab">
        <BauIcone tamanho={52} aberto={b.aberto} />
        <p>
          <b>Baú do dia {b.dia}</b>
          <small>{b.aberto ? `Aberto hoje. Amanhã: ${conteudo(amanha)}.` : b.jogouHoje ? `Hoje: ${conteudo(hoje)}.` : `Termine uma partida hoje para abrir: ${conteudo(hoje)}.`}</small>
        </p>
      </div>
      <ol className="calendario-bau" aria-label="Calendário do baú, 7 dias">
        {b.calendario.map((c) => <DiaDoCalendario key={c.dia} c={c} hoje={b.dia} aberto={b.aberto} />)}
      </ol>
      {b.disponivel && <button className="btn1 largo" disabled={ocupado} onClick={() => abrir('dia')}>ABRIR O BAÚ</button>}
      {!b.jogouHoje && !b.aberto && (ligaNoAr()
        ? <button className="btn2 largo" onClick={() => ir({ tela: 'liga', trio: null })}>Jogar na liga</button>
        : <button className="btn2 largo" onClick={() => ir({ tela: 'inicio' })}>Ver salas abertas</button>)}
      <p className="nota">O conteúdo de cada dia é fixo. Perder um dia volta o calendário ao dia 1.</p>
    </section>
  );
}

function BauDeRetorno({ m, ocupado, abrir }: { m: PainelMissoes; ocupado: boolean; abrir: (tipo: 'dia' | 'retorno') => void }) {
  if (!m.bau.retorno.disponivel) return null;
  return (
    <section className="cartao bau-retorno" aria-label="Baú de retorno">
      <div className="bau-cab">
        <BauIcone tamanho={52} />
        <p><b>Que bom te ver de volta</b><small>Baú de retorno: +{m.bau.retorno.rodagem} de Rodagem.</small></p>
      </div>
      <button className="btn1 largo" disabled={ocupado} onClick={() => abrir('retorno')}>ABRIR O BAÚ DE RETORNO</button>
    </section>
  );
}

function LinhaDaMissao({ x, trocar }: { x: MissaoNaTela; trocar?: () => void }) {
  return (
    <li className={'missao' + (x.concluida ? ' feita' : '')}>
      <div className="missao-cab">
        <span className="check" aria-hidden="true">{x.concluida ? '✓' : ''}</span>
        <p><b>{x.titulo}</b><small>{x.concluida ? 'Concluída' : `${x.progresso} de ${x.meta}`}</small></p>
        <span className="premio-missao">+{x.rodagem}</span>
      </div>
      <Progresso valor={x.progresso} meta={x.meta} rotulo={`${x.titulo}: ${x.progresso} de ${x.meta}`} fina />
      {trocar && !x.concluida && <button className="link trocar" onClick={trocar}>Trocar esta missão</button>}
    </li>
  );
}

export function MissoesTela({ sessao }: { sessao: Sessao }) {
  const [m, setM] = useState<PainelMissoes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    let vivo = true;
    lerMissoes(sessao).then((x) => { if (vivo) setM(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  async function agir(fn: () => Promise<void>) {
    setOcupado(true); setErro(null); setAviso(null);
    try { await fn(); } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }
  const abrir = (tipo: 'dia' | 'retorno') => agir(async () => {
    const r = await abrirBau(sessao, tipo);
    setM(r.painel);
    if (r.paga) setAviso(r.paga.tentos ? `Você ganhou ${r.paga.tentos} Tentos.` : r.paga.rodagem ? `Você ganhou +${r.paga.rodagem} de Rodagem.` : 'Baú aberto. O teto de Tentos do mês já foi alcançado.');
  });
  const trocar = (slot: number) => agir(async () => { setM(await trocarMissao(sessao, slot)); });
  return (
    <Tela larga>
      <Topo titulo="Missões" voltar={voltar} />
      <div className="missoes-tela">
        <div className="missoes-lado">
          {erro && <p className="erro" role="alert">{erro}</p>}
          {aviso && <p className="ok" role="status">{aviso}</p>}
          {!m && !erro && <p className="carregando" role="status">Um instante...</p>}
          {m && <BauDeRetorno m={m} ocupado={ocupado} abrir={abrir} />}
          {m && <BauDoDia m={m} ocupado={ocupado} abrir={abrir} />}
        </div>
        {m && (
          <div className="missoes-lado">
            <section className="cartao lista-missoes" aria-label="Missões do dia">
              <h2 className="rotulo dourado">MISSÕES DO DIA</h2>
              <ul>{m.dia.missoes.map((x) => <LinhaDaMissao key={x.slot} x={x} trocar={m.dia.trocaDisponivel && !ocupado ? () => trocar(x.slot) : undefined} />)}</ul>
              <p className="nota">{m.dia.trocaDisponivel ? 'Você pode trocar 1 missão por dia, de graça.' : 'Troca de hoje usada. Amanhã tem outra.'}</p>
            </section>
            <section className="cartao lista-missoes" aria-label="Missões da semana">
              <h2 className="rotulo dourado">MISSÕES DA SEMANA</h2>
              <ul>{m.semana.missoes.map((x) => <LinhaDaMissao key={x.slot} x={x} />)}</ul>
              <p className="nota">Valem de segunda a domingo.</p>
            </section>
            <PasseNoPerfil />
          </div>
        )}
      </div>
    </Tela>
  );
}

// Início: as missões do dia e o baú, num toque para a tela Missões.
export function MissoesNoInicio({ m }: { m: PainelMissoes }) {
  const feitas = m.dia.missoes.filter((x) => x.concluida).length, total = m.dia.missoes.length;
  const bau = m.bau.retorno.disponivel ? 'Baú de retorno pronto' : m.bau.disponivel ? 'Baú pronto para abrir' : m.bau.aberto ? 'Baú de hoje aberto' : 'Baú abre depois da partida';
  const pronto = m.bau.disponivel || m.bau.retorno.disponivel;
  return (
    <button className={'missoes-inicio' + (pronto ? ' pronto' : '')} onClick={() => ir({ tela: 'missoes' })}>
      <BauIcone tamanho={34} aberto={m.bau.aberto && !m.bau.retorno.disponivel} />
      <span>
        <b>Missões do dia: {feitas} de {total}</b>
        <small>{bau}</small>
      </span>
      <Icone nome="seta" tamanho={16} />
    </button>
  );
}

// Fim de partida: as missões que a partida fechou. GA4 missao_concluida uma vez por missão.
export function MissoesDaPartida({ ms, partida }: { ms: MissaoFechada[]; partida: string }) {
  useEffect(() => {
    for (const x of ms) medirUmaVez(`missao:${partida}:${x.id}`, 'missao_concluida', { missao: x.id, periodo: x.periodo });
  }, [partida, ms.length]);
  if (!ms.length) return null;
  return (
    <section className="cartao missoes-fim" aria-label="Missões concluídas">
      <b className="rotulo dourado">{ms.length === 1 ? 'MISSÃO CONCLUÍDA' : `${ms.length} MISSÕES CONCLUÍDAS`}</b>
      <ul>{ms.map((x) => <li key={x.periodo + x.id}><span>✓ {x.titulo}</span><b>+{x.rodagem}</b></li>)}</ul>
    </section>
  );
}
